import { Dictionary } from 'lodash';
import differenceBy from 'lodash/differenceBy';
import intersectionBy from 'lodash/intersectionBy';
import {
  MemberType,
  PermissionSetType,
  Squad,
  Team,
  User,
  UserType,
} from 'api/models';
import { AssigneeValue } from 'api/tasks';
import { SquadData } from 'components/molecules/squad-picker/use-squad-options';
import { MembersResponse } from 'data/members/api';
import { getRandomColor } from 'utils/get-random-color';
import { getUserLabel } from 'utils/get-user-label';
import {
  Option,
  SquadPayload,
  SquadUpdateActionPayload,
  TeamOption,
  UserOption,
} from './types';

const USER_PREFIX = 'user_';
const TEAM_PREFIX = 'team_';

const toValue = (value: string | number, prefix = '') => {
  return prefix + value?.toString();
};

export const userOption = (user: User, isInvited = true): UserOption => {
  return {
    label: getUserLabel(user) || '',
    value: toValue(user?.userId, USER_PREFIX),
    imageUrl: user?.photoUrl,
    description: user?.role?.roleName,
    logoUrl: user?.company?.companyLogoUrl,
    color: getRandomColor(user?.userId),
    extra: {
      ...user,
      type: 'user',
      id: user?.userId,
      timeOffDetails: user?.userTimeOffDetails,
      holidayCalendarId: user?.holidayCalendarId,
    },
    invited: isInvited,
    userType: user?.userType,
    userStatus: user?.userStatus,
    roleId: user?.role?.roleId,
    userTimeOffDetails: user?.userTimeOffDetails,
    holidayCalendarId: user?.holidayCalendarId,
  };
};

export const userEmailOption = (user: User, isInvited = true): UserOption => {
  return {
    label: getUserLabel(user) || '',
    value: user?.emailId,
    imageUrl: user?.photoUrl,
    description: user?.role?.roleName,
    logoUrl: user?.company?.companyLogoUrl,
    color: getRandomColor(user?.userId),
    extra: {
      ...user,
      holidayCalendarId: undefined,
      type: 'user',
      id: user?.emailId as unknown as number,
      timeOffDetails: user?.userTimeOffDetails,
    },
    invited: isInvited,
    userType: user?.userType,
    userStatus: user?.userStatus,
    roleId: user?.role?.roleId,
    userTimeOffDetails: user?.userTimeOffDetails,
  };
};

export const teamOption = (team: Team): TeamOption => {
  return {
    label: team?.teamName || '',
    value: toValue(team?.teamId, TEAM_PREFIX),
    color: getRandomColor(team?.teamId),
    extra: { ...team, type: 'team', id: team?.teamId },
    invited: true,
    roleId: team?.roleId,
    teamType: team?.teamType,
  };
};

export const PROJECT_OWNER_OPTION: TeamOption = {
  extra: { id: -121, type: 'team' },
  label: 'Project owner',
  value: 'team_-121',
};

export const PROJECT_OWNER_PLACEHOLDER: Team = {
  teamId: -121,
  teamName: 'Project owner',
  teamType: MemberType.Native,
  roleId: -121,
};

export const ALL_PROJECT_MEMBERS_OPTION: TeamOption = {
  extra: { id: -122, type: 'team' },
  label: 'All Project Members',
  value: 'team_-122',
};

export const ALL_PROJECT_MEMBERS_PLACEHOLDER: Team = {
  teamId: -122,
  teamName: 'All Project Members',
  teamType: MemberType.Native,
  roleId: -122,
};

export const CUSTOMER_PROJECT_MEMBERS_OPTION: TeamOption = {
  extra: { id: -123, type: 'team' },
  label: 'Customer Project Members',
  value: 'team_-123',
};

export const CUSTOMER_PROJECT_MEMBERS_PLACEHOLDER: Team = {
  teamId: -123,
  teamName: 'Customer Project Members',
  teamType: MemberType.External,
  roleId: -123,
};

export const userTypeOption = (option: Option) => {
  return option.extra.type === 'user';
};

export const teamTypeOption = (option: Option) => {
  return option.extra.type === 'team';
};

export const filterUserOptions = (options: Option[] = []): UserOption[] => {
  return options.filter(userTypeOption) as UserOption[];
};

export const nonProjectUsers = (option: UserOption) => {
  return !option.invited;
};

export const invitableUser = <
  UserOption extends { extra: { id: number }; userType: UserType },
>(
  o: UserOption,
) => {
  return {
    user: {
      userId: o.extra.id,
    },
    permissionSet: {
      permissionSetType:
        o.userType === UserType.External
          ? PermissionSetType.CustomerChampion
          : PermissionSetType.Collaborator,
    },
  };
};

export const squad = (
  value: Partial<AssigneeValue> | undefined | null,
  userLookup?: Dictionary<User>,
): AssigneeValue => {
  return {
    users:
      (userLookup
        ? value?.users?.map((user) => ({
            ...user,
            userTimeOffDetails: userLookup[user?.userId]?.userTimeOffDetails,
          }))
        : value?.users) || [],
    teams: value?.teams || [],
  };
};

export const squadToOptions = ({ users, teams }: AssigneeValue): Option[] => {
  const usersOptions = users?.map((u) => userOption(u)) || [];
  const teamOptions = teams?.map(teamOption) || [];

  return [...usersOptions, ...teamOptions];
};

export const optionsToSquadPayload = (options: Option[] = []): SquadPayload => {
  const userPayload = (o: Option) => ({ userId: o.extra.id });
  const teamPayload = (o: Option) => ({ teamId: o.extra.id });
  const users = options.filter((o) => o.extra.type === 'user').map(userPayload);
  const teams = options.filter((o) => o.extra.type === 'team').map(teamPayload);

  return {
    users,
    teams,
  };
};

export const squadPayloadToSquad = (
  payload: SquadPayload,
  squadSource: Squad,
): Squad => {
  const userIds = payload.users.map(({ userId }) => userId);
  const teamIds = payload.teams.map(({ teamId }) => teamId);
  const users = squadSource.users.filter(({ userId }) =>
    userIds.includes(userId),
  );
  const teams = squadSource.teams.filter(({ teamId }) =>
    teamIds.includes(teamId),
  );

  return { users, teams };
};

export const optionsToSquad = (
  options: Option[],
  squadSource: Squad,
): Squad => {
  const squadPayload = optionsToSquadPayload(options);

  return squadPayloadToSquad(squadPayload, squadSource);
};

export const payload = (
  newValue: Option[] = [],
  oldValue: Option[] = [],
): SquadUpdateActionPayload => {
  if (oldValue.length !== 0 && newValue.length === 0) {
    return {
      remove: optionsToSquadPayload(oldValue),
    };
  }

  if (oldValue.length === 0 && newValue.length !== 0) {
    return {
      add: optionsToSquadPayload(newValue),
    };
  }

  const oldValues = oldValue.map((o) => o.value);
  const selector = (option: Option) => option.value;
  const newlyAdded = newValue.filter((o) => !oldValues.includes(o.value));
  const existingValue = intersectionBy(oldValue, newValue, selector);
  const removed = differenceBy(oldValue, existingValue, selector);

  return {
    add: optionsToSquadPayload(newlyAdded),
    remove: optionsToSquadPayload(removed),
  };
};

export const squadToMembersResponse = ({
  users,
  teams,
}: SquadData): MembersResponse => {
  return {
    users,
    teams,
    members: [],
  };
};
